import { useEffect, useState, useCallback } from "react";
import { Routes, Route, NavLink, Link, useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Inbox, Users, ListChecks, BookOpen, Loader2 } from "lucide-react";
import MishapResultsView from "@/components/MishapResultsView";

/* Phase D — Adviser workbench (single-file portal, view-switched by nested route) */

const STAGES = ["prospect", "onboarding", "active", "paused", "transferred", "churned"];
const TASK_STATUSES = ["open", "done", "snoozed"];
const TOUCH_KINDS = ["email", "call", "meeting", "portal_message", "other"];

export default function AdviserPortal() {
  const { user, loading } = useAuth();
  if (loading) return <Layout><div className="p-16 text-stone">Loading…</div></Layout>;
  if (!user || !["adviser", "admin"].includes(user.role)) {
    return <Layout><div className="max-w-3xl mx-auto p-16"><h1 className="font-serif text-3xl text-ivy">Adviser access required</h1><p className="text-stone mt-3">Sign in as an adviser or admin to continue.</p><Link to="/login" className="mt-4 inline-block text-brass border-b border-brass">Sign in →</Link></div></Layout>;
  }
  return (
    <Layout>
      <SEO noindex title="Adviser Portal | Beyond Valencia" canonical="/adviser" />
      <div className="max-w-7xl mx-auto grid lg:grid-cols-[220px_1fr] gap-8 px-5 sm:px-8 py-10" data-testid="adviser-portal">
        <aside className="border border-stone/30 bg-bone-dim/20 p-5 h-fit">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold mb-4">Adviser</div>
          <nav className="space-y-1">
            {[
              { to: "/adviser", end: true, icon: Inbox, label: "Inbox" },
              { to: "/adviser/clients", icon: Users, label: "Clients" },
              { to: "/adviser/tasks", icon: ListChecks, label: "My Tasks" },
              { to: "/adviser/directory", icon: BookOpen, label: "Directory" },
            ].map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm border-l-2 ${isActive ? "border-brass bg-brass/10 text-ivy" : "border-transparent text-ink-soft hover:text-ivy hover:bg-bone/40"}`} data-testid={`adv-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}>
                <l.icon className="h-4 w-4" />{l.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-6 pt-4 border-t border-stone/30 text-[10px] text-stone">
            <div className="uppercase tracking-[0.2em]">Signed in as</div>
            <div className="text-ink text-sm mt-1">{user.name}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brass mt-0.5">{user.role}</div>
          </div>
        </aside>
        <main>
          <Routes>
            <Route index element={<InboxView me={user} />} />
            <Route path="clients" element={<ClientsList />} />
            <Route path="clients/:client_id" element={<ClientDetail me={user} />} />
            <Route path="tasks" element={<MyTasks me={user} />} />
            <Route path="directory" element={<Directory me={user} />} />
          </Routes>
        </main>
      </div>
    </Layout>
  );
}

/* ============================ INBOX ============================ */
function InboxView({ me }) {
  const [inbox, setInbox] = useState(null);
  useEffect(() => { api.get("/adviser/inbox").then((r) => setInbox(r.data)).catch(() => toast.error("Inbox load failed")); }, []);
  if (!inbox) return <div className="text-stone">Loading inbox…</div>;
  return (
    <div className="space-y-8" data-testid="adv-inbox">
      <div>
        <h1 className="font-serif text-3xl text-ivy tracking-tight">Inbox</h1>
        <p className="text-sm text-stone mt-1">Signed in as {me.name}</p>
      </div>
      <TaskSection title="Overdue" tasks={inbox.overdue} tone="crimson" empty="Nothing overdue" testId="adv-inbox-overdue" />
      <TaskSection title="Due today" tasks={inbox.due_today} empty="Nothing due today" testId="adv-inbox-today" />
      <TaskSection title="This week" tasks={inbox.this_week} empty="Nothing this week" testId="adv-inbox-week" />
      <div data-testid="adv-inbox-unassigned">
        <div className="text-[10px] uppercase tracking-[0.22em] text-brass font-semibold mb-3">Unassigned prospects <span className="ml-1.5 text-stone">{inbox.unassigned_clients?.length || 0}</span></div>
        {(inbox.unassigned_clients?.length || 0) === 0 ? (
          <div className="border border-stone/30 bg-bone-dim/30 px-3 py-2.5 text-[11px] text-stone italic">No unassigned prospects</div>
        ) : (
          <ul className="space-y-2">
            {inbox.unassigned_clients.map((c) => (
              <li key={c.client_id} className="border border-stone/30 bg-bone p-3 flex items-baseline justify-between">
                <div>
                  <div className="text-ivy font-medium text-sm flex items-center gap-2">
                    {c.heat_tag === "hot" && <span className="inline-block w-2 h-2 bg-crimson" data-testid={`adv-hot-dot-${c.client_id}`} />}
                    {c.name}
                  </div>
                  <div className="text-[11px] text-stone">{c.email} · {c.stage}</div>
                </div>
                <Link to={`/adviser/clients/${c.client_id}`} className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-brass-soft">Claim →</Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function TaskSection({ title, tasks, empty, tone = "brass", testId }) {
  const isCrimson = tone === "crimson";
  return (
    <div data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-3 ${isCrimson ? "text-crimson" : "text-brass"}`}>
        {title} <span className={`ml-1.5 ${isCrimson ? "text-crimson" : "text-stone"}`}>{tasks?.length || 0}</span>
      </div>
      {(tasks?.length || 0) === 0 ? (
        <div className="border border-stone/30 bg-bone-dim/30 px-3 py-2.5 text-[11px] text-stone italic">{empty}</div>
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => (
            <li key={t.task_id} className="border border-stone/30 bg-bone p-3">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-ivy font-medium text-sm">{t.title}</div>
                  <div className="text-[11px] text-stone">{t.client?.name || t.client?.email} · Due {t.due_date?.slice(0, 10) || "—"}</div>
                </div>
                <Link to={`/adviser/clients/${t.client_id}`} className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-brass-soft">Open →</Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================ CLIENTS LIST ============================ */
function ClientsList() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [scope, setScope] = useState("mine");
  const [heatTag, setHeatTag] = useState("");
  const load = useCallback(() => {
    api.get("/adviser/clients", { params: { q, stage: stage || undefined, adviser_id: scope || undefined, heat_tag: heatTag || undefined, page_size: 100 } })
      .then((r) => setRows(r.data.items || []));
  }, [q, stage, scope, heatTag]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="adv-clients-list">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="font-serif text-3xl text-ivy tracking-tight">Clients</h1>
        <div className="text-sm text-stone">{rows.length} shown</div>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <input type="search" placeholder="Search email or name…" value={q} onChange={(e) => setQ(e.target.value)} className="border border-stone/40 bg-bone px-3 py-2 text-sm outline-none focus:border-ivy min-w-[220px]" />
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="border border-stone/40 bg-bone px-3 py-2 text-sm">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex border border-stone/40" data-testid="adv-heat-filter">
          {[["", "All heat"], ["hot", "Hot"], ["warm", "Warm"], ["cold", "Cold"]].map(([v, l]) => (
            <button key={v || "all"} onClick={() => setHeatTag(v)} className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 ${heatTag === v ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`} data-testid={`adv-heat-${v || "all"}`}>{l}</button>
          ))}
        </div>
        <div className="flex border border-stone/40">
          {[["mine", "Mine"], ["", "All"]].map(([v, l]) => (
            <button key={v} onClick={() => setScope(v)} className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 ${scope === v ? "bg-ivy text-bone" : "text-ink hover:bg-stone/10"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="border border-stone/30 bg-bone">
        <table className="w-full text-sm">
          <thead className="bg-bone-dim/40 text-[10px] uppercase tracking-[0.18em] text-brass">
            <tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Email</th><th className="text-left px-4 py-2.5">Heat</th><th className="text-left px-4 py-2.5">Stage</th><th className="text-left px-4 py-2.5">Last touch</th><th className="text-right px-4 py-2.5"></th></tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.client_id} className="border-t border-stone/20 hover:bg-bone-dim/20">
                <td className="px-4 py-2.5 text-ink flex items-center gap-2">
                  {c.heat_tag === "hot" && <span className="inline-block w-2 h-2 bg-crimson" data-testid={`adv-hot-dot-list-${c.client_id}`} />}
                  {c.name}
                </td>
                <td className="px-4 py-2.5 text-ink-soft">{c.email}</td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-[0.18em]">
                  {c.heat_tag ? <span className={c.heat_tag === "hot" ? "text-crimson font-semibold" : c.heat_tag === "warm" ? "text-brass font-semibold" : "text-stone"}>{c.heat_tag}</span> : <span className="text-stone">—</span>}
                </td>
                <td className="px-4 py-2.5 text-[10px] uppercase tracking-[0.18em] text-brass">{c.stage}</td>
                <td className="px-4 py-2.5 text-stone text-xs">{c.last_touch_at?.slice(0, 10) || "—"}</td>
                <td className="px-4 py-2.5 text-right"><Link to={`/adviser/clients/${c.client_id}`} className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-brass-soft" data-testid={`adv-client-open-${c.client_id}`}>Open →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================ CLIENT DETAIL ============================ */

/* Phase H.2 — mishap analyses for this client with adviser-only notes */
function ClientMishapAnalyses({ clientId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [noteBody, setNoteBody] = useState("");
  const load = useCallback(() => {
    api.get(`/mishap/adviser/clients/${clientId}/mishap-analyses`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!noteBody.trim() || !open) return;
    await api.post(`/mishap/adviser/mishap-analyses/${open.analysis_id}/notes`, { body_markdown: noteBody });
    setNoteBody("");
    toast.success("Note added");
    load();
  };

  const countSev = (m, s) => (m || []).filter((x) => x.severity === s).length;

  return (
    <div className="mt-8 border-t border-stone/30 pt-6" data-testid="adv-client-mishap-analyses">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-serif text-xl text-ivy tracking-tight">Mishap analyses</h2>
        <span className="text-[10px] uppercase tracking-[0.18em] text-brass">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="border border-stone/30 bg-bone-dim/30 px-3 py-2.5 text-[11px] text-stone italic">No mishap analyses for this client yet.</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.analysis_id} className="border border-stone/30 bg-bone p-3" data-testid={`adv-mishap-row-${r.analysis_id}`}>
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-ivy font-medium text-sm">{r.program_slug} · <span className="text-stone text-xs tabular-nums">{(r.generated_at || "").slice(0, 10)}</span></div>
                  <div className="mt-1 space-x-1">
                    {countSev(r.mishaps, "critical") > 0 && <span className="inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold bg-crimson text-bone">{countSev(r.mishaps, "critical")} crit</span>}
                    {countSev(r.mishaps, "high") > 0 && <span className="inline-block px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold bg-brass text-ivy">{countSev(r.mishaps, "high")} high</span>}
                    <span className="text-[10px] text-stone uppercase">· extra sem {r.extra_semesters_added}</span>
                    {(r.notes && r.notes.length > 0) && <span className="text-[10px] text-ivy">· {r.notes.length} note{r.notes.length === 1 ? "" : "s"}</span>}
                  </div>
                </div>
                <button onClick={() => setOpen(open?.analysis_id === r.analysis_id ? null : r)} className="text-[11px] uppercase tracking-[0.18em] text-brass hover:text-brass-soft" data-testid={`adv-mishap-open-${r.analysis_id}`}>{open?.analysis_id === r.analysis_id ? "Close" : "View"} →</button>
              </div>
              {r.transcript_id && (
                <div className="mt-2">
                  <button
                    onClick={async () => {
                      try {
                        const resp = await api.get(`/adviser/transcripts/${r.transcript_id}/download`, { responseType: "blob" });
                        const url = URL.createObjectURL(resp.data);
                        const a = document.createElement("a"); a.href = url;
                        a.download = `transcript_${r.transcript_id}.pdf`; a.click();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        const d = err?.response?.data;
                        toast.error(typeof d === "string" ? d : "No PDF (manual entry) or download failed.");
                      }
                    }}
                    className="text-[10px] uppercase tracking-[0.18em] text-brass hover:text-ivy border border-brass/40 px-2 py-1"
                    data-testid={`adv-mishap-transcript-dl-${r.analysis_id}`}
                  >Download transcript PDF →</button>
                </div>
              )}
              {open?.analysis_id === r.analysis_id && (
                <div className="mt-4 border-t border-stone/30 pt-4" data-testid={`adv-mishap-detail-${r.analysis_id}`}>
                  <MishapResultsView analysis={r} showPrintCTA={false} />
                  <div className="mt-6 border border-stone/30 bg-bone-dim/30 p-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-brass mb-2">Adviser note (internal only)</div>
                    <textarea rows={3} value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Internal note (never shown to student)…" className="w-full border border-stone/40 bg-bone px-2 py-1 text-sm resize-y mb-2" data-testid="adv-mishap-note-body" />
                    <button onClick={addNote} className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[10px] font-semibold py-1.5 px-4" data-testid="adv-mishap-note-submit">Add note</button>
                  </div>
                  {(r.notes || []).length > 0 && (
                    <ul className="mt-4 space-y-2">
                      {r.notes.map((n) => (
                        <li key={n.note_id} className="border-l-2 border-ivy pl-3 py-1">
                          <div className="text-[10px] uppercase tracking-[0.22em] text-brass">{n.author_name || n.author_user_id} · {(n.created_at || "").slice(0, 10)}</div>
                          <div className="text-sm text-ink mt-1">{n.body_markdown}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ClientDetail({ me }) {
  const { client_id } = useParams();
  const [d, setD] = useState(null);
  const [noteBody, setNoteBody] = useState("");
  const [noteKind, setNoteKind] = useState("internal");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDue, setTaskDue] = useState("");
  const [touchKind, setTouchKind] = useState("email");
  const [touchSummary, setTouchSummary] = useState("");
  const load = useCallback(() => api.get(`/adviser/clients/${client_id}`).then((r) => setD(r.data)).catch(() => toast.error("Load failed")), [client_id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-stone">Loading client…</div>;
  const { client, user, lead, plans, notes, tasks, touchpoints, milestone_states } = d;
  const stateFor = (plan_id, key) => milestone_states.find((m) => m.plan_id === plan_id && m.milestone_key === key)?.state || "pending";
  const timeline = [
    ...notes.map((n) => ({ ...n, kind: "note", ts: n.created_at })),
    ...touchpoints.map((t) => ({ ...t, kind: "touch", ts: t.occurred_at })),
    ...plans.map((p) => ({ ts: p.created_at, kind: "plan", plan_id: p.plan_id, summary: (p.plan_json || {}).summary })),
  ].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));

  const claim = async () => {
    await api.post(`/adviser/clients/${client_id}/assign`, { adviser_id: me.user_id });
    toast.success("Assigned to you"); load();
  };
  const setStage = async (stage) => { await api.post(`/adviser/clients/${client_id}/stage`, { stage }); load(); };
  const addNote = async () => {
    if (!noteBody.trim()) return;
    await api.post(`/adviser/clients/${client_id}/notes`, { body_markdown: noteBody, kind: noteKind });
    setNoteBody(""); toast.success("Note added"); load();
  };
  const addTask = async () => {
    if (!taskTitle.trim()) return;
    await api.post(`/adviser/clients/${client_id}/tasks`, { title: taskTitle, due_date: taskDue || null });
    setTaskTitle(""); setTaskDue(""); toast.success("Task added"); load();
  };
  const logTouch = async () => {
    if (!touchSummary.trim()) return;
    await api.post(`/adviser/clients/${client_id}/touchpoints`, { kind: touchKind, summary: touchSummary });
    setTouchSummary(""); toast.success("Touchpoint logged"); load();
  };
  const markMilestone = async (plan_id, milestone_key, state) => {
    await api.post(`/adviser/plans/${plan_id}/milestones/${milestone_key}/state`, { state });
    load();
  };
  const flipTask = async (task_id, status) => { await api.post(`/adviser/tasks/${task_id}/status`, { status }); load(); };

  return (
    <div className="grid lg:grid-cols-[240px_1fr_280px] gap-6" data-testid="adv-client-detail">
      {/* LEFT */}
      <div className="border border-stone/30 bg-bone p-5 h-fit space-y-4">
        <div>
          <h2 className="font-serif text-xl text-ivy tracking-tight">{client.name}</h2>
          <p className="text-xs text-stone">{client.email}</p>
        </div>
        <div><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Stage</div>
          <select value={client.stage} onChange={(e) => setStage(e.target.value)} className="mt-1 border border-stone/40 bg-bone px-2 py-1 text-xs uppercase tracking-[0.14em] w-full" data-testid="adv-client-stage-select">
            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Tier</div><div className="text-sm text-ink">{client.tier || "—"}</div></div>
        <div><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Adviser</div>
          <div className="text-sm text-ink">{client.adviser_id ? (client.adviser_id === me.user_id ? "You" : client.adviser_id) : (
            <button onClick={claim} className="text-brass border-b border-brass hover:text-brass-soft" data-testid="adv-client-claim">Claim →</button>
          )}</div>
        </div>
        <div className="pt-3 border-t border-stone/30">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass mb-2">Log touchpoint</div>
          <select value={touchKind} onChange={(e) => setTouchKind(e.target.value)} className="border border-stone/40 bg-bone px-2 py-1 text-xs w-full mb-2">
            {TOUCH_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <textarea rows={2} value={touchSummary} onChange={(e) => setTouchSummary(e.target.value)} placeholder="Brief summary…" className="border border-stone/40 bg-bone px-2 py-1 text-xs w-full mb-2 resize-y" />
          <button onClick={logTouch} className="w-full bg-brass hover:bg-brass-soft text-ink text-[10px] uppercase tracking-[0.18em] font-semibold py-1.5" data-testid="adv-log-touch">Log</button>
        </div>
        {lead?.intake_data && <details className="pt-3 border-t border-stone/30"><summary className="text-[10px] uppercase tracking-[0.22em] text-brass cursor-pointer">Intake</summary><pre className="text-[10px] text-ink-soft mt-2 whitespace-pre-wrap max-h-48 overflow-y-auto">{JSON.stringify(lead.intake_data, null, 2)}</pre></details>}
      </div>

      {/* MIDDLE — timeline */}
      <div>
        <h1 className="font-serif text-2xl text-ivy tracking-tight mb-4">Timeline</h1>
        <div className="border border-stone/30 bg-bone p-4 mb-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass mb-2">Add note</div>
          <select value={noteKind} onChange={(e) => setNoteKind(e.target.value)} className="border border-stone/40 bg-bone px-2 py-1 text-xs mb-2">
            <option value="internal">Internal (not visible to client)</option>
            <option value="summary">Summary (visible to client)</option>
            <option value="followup_needed">Followup needed</option>
          </select>
          <textarea rows={3} value={noteBody} onChange={(e) => setNoteBody(e.target.value)} placeholder="Note body (markdown)…" className="w-full border border-stone/40 bg-bone px-2 py-1 text-sm resize-y mb-2" data-testid="adv-note-body" />
          <button onClick={addNote} className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[10px] font-semibold py-1.5 px-4" data-testid="adv-note-submit">Add note</button>
        </div>
        <ul className="space-y-3" data-testid="adv-timeline">
          {timeline.map((e, i) => (
            <li key={i} className="border-l-2 border-stone/40 pl-4 py-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brass">
                {e.kind === "note" ? `Note · ${e.kind_}` : e.kind === "touch" ? `Touch · ${e.kind_ || e.kind}` : "Plan generated"} · {(e.ts || "").slice(0, 10)}
              </div>
              {e.kind === "note" && <div className="text-sm text-ink mt-1">{e.body_markdown}</div>}
              {e.kind === "touch" && <div className="text-sm text-ink mt-1">{e.summary} <span className="text-[10px] text-stone uppercase">({e.kind_ || "—"})</span></div>}
              {e.kind === "plan" && <div className="text-sm text-ink-soft mt-1 italic">{e.summary?.slice(0, 200)}… <Link to={`/my-plan/${e.plan_id}`} className="text-brass border-b border-brass/40">Open plan →</Link></div>}
            </li>
          ))}
        </ul>

        <ClientMishapAnalyses clientId={client_id} />
      </div>

      {/* RIGHT — tasks + milestones */}
      <div className="space-y-6">
        <div className="border border-stone/30 bg-bone p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass mb-3">Tasks</div>
          <ul className="space-y-2 mb-3">
            {tasks.map((t) => (
              <li key={t.task_id} className="text-xs">
                <div className="text-ink">{t.title}</div>
                <div className="flex items-center gap-2 mt-1">
                  {TASK_STATUSES.map((s) => (
                    <button key={s} onClick={() => flipTask(t.task_id, s)} className={`text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 border ${t.status === s ? "bg-ivy text-bone border-ivy" : "text-ink border-stone/40"}`}>{s}</button>
                  ))}
                </div>
              </li>
            ))}
            {tasks.length === 0 && <li className="text-[11px] text-stone italic">No tasks yet.</li>}
          </ul>
          <input type="text" placeholder="New task…" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} className="w-full border border-stone/40 bg-bone px-2 py-1 text-xs mb-2" />
          <input type="date" value={taskDue} onChange={(e) => setTaskDue(e.target.value)} className="w-full border border-stone/40 bg-bone px-2 py-1 text-xs mb-2" />
          <button onClick={addTask} className="w-full bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[10px] font-semibold py-1.5" data-testid="adv-task-add">+ Task</button>
        </div>
        {plans.length === 0 ? (
          <div className="border border-stone/30 bg-bone-dim/30 p-4" data-testid="adv-milestones-empty">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass mb-2">Plan milestones</div>
            <div className="text-[11px] text-stone italic">No plan generated yet.</div>
          </div>
        ) : (
          <div className="border border-stone/30 bg-bone p-4" data-testid="adv-milestones">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass mb-3">Plan milestones</div>
            {plans.map((p) => {
              const grouped = { next_30_days: [], this_semester: [], next_semester: [], before_transfer: [] };
              (p.plan_json?.milestones || []).forEach((m, idx) => {
                const bucket = grouped[m.when] ? m.when : "this_semester";
                grouped[bucket].push({ ...m, idx });
              });
              return (
                <div key={p.plan_id} className="mb-4">
                  <Link to={`/my-plan/${p.plan_id}`} className="text-[11px] text-brass border-b border-brass/40">Plan {p.plan_id.slice(-6)}</Link>
                  {["next_30_days", "this_semester", "next_semester", "before_transfer"].map((w) => grouped[w].length === 0 ? null : (
                    <div key={w} className="mt-3">
                      <div className="text-[9px] uppercase tracking-[0.22em] text-stone">{w.replace(/_/g, " ")}</div>
                      <ul className="mt-1 space-y-1.5">
                        {grouped[w].map(({ idx, title }) => {
                          const key = `m_${idx}`, cur = stateFor(p.plan_id, key);
                          const change = async (next) => {
                            const prev = cur;
                            // Optimistic update
                            setD((old) => ({ ...old, milestone_states: [
                              ...(old?.milestone_states || []).filter((s) => !(s.plan_id === p.plan_id && s.milestone_key === key)),
                              { plan_id: p.plan_id, milestone_key: key, state: next },
                            ] }));
                            try {
                              await api.post(`/adviser/plans/${p.plan_id}/milestones/${key}/state`, { state: next });
                            } catch (e) {
                              // Rollback
                              setD((old) => ({ ...old, milestone_states: [
                                ...(old?.milestone_states || []).filter((s) => !(s.plan_id === p.plan_id && s.milestone_key === key)),
                                { plan_id: p.plan_id, milestone_key: key, state: prev },
                              ] }));
                              toast.error("Update failed — reverted");
                            }
                          };
                          return (
                            <li key={idx} className="text-[11px]">
                              <div className="text-ink">{title}</div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[9px] uppercase tracking-[0.18em] text-brass">{cur}</span>
                                <select value={cur} onChange={(e) => change(e.target.value)} className="text-[10px] border border-stone/40 bg-bone px-1 py-0.5" data-testid={`adv-milestone-select-${p.plan_id}-${key}`}>
                                  {["pending", "in_progress", "done", "skipped"].map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================ TASKS ============================ */
function MyTasks({ me }) {
  const [inbox, setInbox] = useState(null);
  useEffect(() => { api.get("/adviser/inbox").then((r) => setInbox(r.data)); }, []);
  if (!inbox) return <div className="text-stone">Loading…</div>;
  return (
    <div className="space-y-8" data-testid="adv-my-tasks">
      <h1 className="font-serif text-3xl text-ivy tracking-tight">My Tasks</h1>
      <TaskSection title="Overdue" tasks={inbox.overdue} tone="crimson" empty="Nothing overdue" testId="adv-tasks-overdue" />
      <TaskSection title="Due today" tasks={inbox.due_today} empty="Nothing due today" testId="adv-tasks-today" />
      <TaskSection title="This week" tasks={inbox.this_week} empty="Nothing this week" testId="adv-tasks-week" />
    </div>
  );
}

/* ============================ DIRECTORY ============================ */
function Directory({ me }) {
  const [rows, setRows] = useState([]);
  const [invite, setInvite] = useState({ email: "", name: "" });
  const load = () => api.get("/adviser/directory").then((r) => setRows(r.data.items || []));
  useEffect(() => { load(); }, []);
  const doInvite = async () => {
    if (!invite.email || !invite.name) return;
    await api.post("/adviser/directory/invite", invite);
    toast.success("Invite recorded (email gated by RESEND_ENABLED)");
    setInvite({ email: "", name: "" }); load();
  };
  return (
    <div data-testid="adv-directory">
      <h1 className="font-serif text-3xl text-ivy tracking-tight mb-6">Directory</h1>
      <ul className="border border-stone/30 bg-bone divide-y divide-stone/20 mb-6">
        {rows.map((r) => (
          <li key={r.user_id} className="p-3 flex items-baseline justify-between text-sm">
            <div><div className="text-ivy">{r.name}</div><div className="text-[11px] text-stone">{r.email}</div></div>
            <span className="text-[10px] uppercase tracking-[0.22em] text-brass">{r.role}</span>
          </li>
        ))}
      </ul>
      {me.role === "admin" && (
        <div className="border border-stone/30 bg-bone p-4">
          <div className="text-[10px] uppercase tracking-[0.22em] text-brass mb-3">Invite new adviser</div>
          <input type="text" placeholder="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="w-full border border-stone/40 bg-bone px-2 py-1.5 text-sm mb-2" />
          <input type="email" placeholder="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="w-full border border-stone/40 bg-bone px-2 py-1.5 text-sm mb-2" />
          <button onClick={doInvite} className="bg-brass hover:bg-brass-soft text-ink uppercase tracking-[0.18em] text-[10px] font-semibold py-2 px-4" data-testid="adv-directory-invite">Send invite</button>
        </div>
      )}
    </div>
  );
}
